import { CollectionViewer } from '@angular/cdk/collections';
import { InMemoryDataSource } from '@app/shared/data-sources/in-memory-data-source';
import { Subscription } from 'rxjs';

import { isVoicemailRead, Voicemail, VoicemailType } from '../models/voicemail.models';
import { VoicemailStorageService } from '../services/voicemail-storage.service';

/**
 * Sentinel value for `mailboxUuid` meaning "owned-only" (excludes shared voicemails).
 */
export const OWNED_VOICEMAIL_SENTINEL = '__owned__';

/**
 * Sentinel value for `mailboxUuid` meaning "we don't know which mailbox this is yet" —
 * used when the route names a mailbox that isn't in the user's known list yet
 * (e.g. shared mailboxes haven't loaded). Predicate filters to zero items so we don't
 * leak owned voicemails into a shared-mailbox view during the brief loading window.
 *
 * The layout escapes this state by including `localVoicemailNumbers$` in its
 * filter-driving combineLatest, so once the shared-mailbox list resolves the
 * subscription re-fires, `resolveMailboxUuid` succeeds, and the filter is
 * replaced with the real uuid.
 */
export const UNKNOWN_MAILBOX_SENTINEL = '__unknown__';

export interface VoicemailFilterOptions {
  /** Search term to filter by label/tel/contact name/contact extension */
  searchTerm?: string | null;

  /** When true, show only trash. When false/undefined, show only non-trash. */
  trashMode?: boolean;

  /** null = both read and unread; true = read only; false = unread only */
  readFilter?: boolean | null;

  /**
   * null = both owned and shared mailboxes.
   * OWNED_VOICEMAIL_SENTINEL = owned only (no sharedInfo).
   * Otherwise: match a specific shared mailbox by `sharedInfo.uuid`.
   */
  mailboxUuid?: string | null;
}

/**
 * DataSource for Voicemail backed by the in-memory cache in VoicemailStorageService.
 *
 * Subscribes to bulkDataChanged$ for live updates, and exposes setFilterOptions()
 * for the layout component to push selected-mailbox / trash / read filters down.
 */
export class VoicemailDataSource extends InMemoryDataSource<Voicemail> {
  private filterOptions: VoicemailFilterOptions = {};

  /** Cached search result IDs when a search term is active */
  private searchResultIds: string[] | null = null;

  /** Subscription to cache changes from storage service */
  private cacheChangedSub?: Subscription;

  constructor(private readonly storageService: VoicemailStorageService) {
    super();
    this.refresh();
    this.cacheChangedSub = this.storageService.bulkDataChanged$.subscribe(() => this.refresh());
  }

  public override disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.cacheChangedSub?.unsubscribe();
  }

  public setFilterOptions(options: VoicemailFilterOptions): void {
    const prev = this.filterOptions;
    if (
      (prev.searchTerm ?? '') === (options.searchTerm ?? '') &&
      !!prev.trashMode === !!options.trashMode &&
      (prev.readFilter ?? null) === (options.readFilter ?? null) &&
      (prev.mailboxUuid ?? null) === (options.mailboxUuid ?? null)
    ) {
      return;
    }

    this.filterOptions = options;

    const term = options.searchTerm?.trim();
    this.searchResultIds = term && term.length > 0 ? this.storageService.search(term) : null;

    this.refresh();
  }

  public getFilterOptions(): VoicemailFilterOptions {
    return { ...this.filterOptions };
  }

  public refresh(): void {
    // Re-query FlexSearch to pick up items added since the last setFilterOptions() call
    const term = this.filterOptions.searchTerm?.trim();
    if (term && term.length > 0) {
      this.searchResultIds = this.storageService.search(term);
    }

    this.emitData(this.getFilteredItems());
  }

  private getFilteredItems(): Voicemail[] {
    const predicate = this.buildPredicate();
    if (this.searchResultIds !== null) {
      let items = this.storageService.getByIds(this.searchResultIds);
      items = items.filter(predicate);
      return items;
    }
    return this.storageService.getFilteredItems(predicate);
  }

  /**
   * Build the runtime filter predicate from the current options.
   *
   * Trash semantics: when trashMode is true, ONLY RecentlyDeleted items pass;
   * when false/undefined, RecentlyDeleted items are excluded.
   */
  private buildPredicate(): (vm: Voicemail) => boolean {
    const { trashMode, readFilter, mailboxUuid } = this.filterOptions;
    return (vm: Voicemail) => {
      if (trashMode) {
        if (vm.type !== VoicemailType.RecentlyDeleted) return false;
      } else {
        if (vm.type === VoicemailType.RecentlyDeleted) return false;
      }
      if (readFilter != null && isVoicemailRead(vm) !== readFilter) return false;
      if (mailboxUuid != null) {
        if (mailboxUuid === OWNED_VOICEMAIL_SENTINEL) {
          if (vm.sharedInfo !== undefined) return false;
        } else if (mailboxUuid === UNKNOWN_MAILBOX_SENTINEL) {
          return false;
        } else if (vm.sharedInfo?.uuid !== mailboxUuid) {
          return false;
        }
      }
      return true;
    };
  }
}
